import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import { useConfiguration } from "../context/ConfigurationContext";
import { useSession } from "../context/SessionContext";

const subscriptionEndpointsDefaults = [
  "/plan_variants",
  "/reseller/subscriptions/create",
  "/subscription",
  "/subscription/create",
  "/subscription/enterprise-quotation-request/success",
  "/subscription/invoice/success",
  "/subscription/overview",
  "/subscriptions",
];

const unsecuredEndpointsDefaults = ["/403", "/404", "/500", "/session_expired"];

const removeBaseUrl = (urls: string[], baseUrl: string) =>
  urls.map((url: string) => {
    if (url.indexOf(baseUrl) === 0) {
      return url.substring(baseUrl.length);
    }
    return url;
  });

type SubscriptionGuardProps = {
  children?: React.ReactNode;
  enforceSubscription?: boolean;
  subscriptionEndpoints?: string[];
  unsecuredEndpoints?: string[];
};

export default function SubscriptionGuard({
  children,
  enforceSubscription = false,
  subscriptionEndpoints = subscriptionEndpointsDefaults,
  unsecuredEndpoints = unsecuredEndpointsDefaults,
}: SubscriptionGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const configuration = useConfiguration();

  const shouldRedirect = (() => {
    if (!session.authenticated) return false;
    const relevantLocationUrl = location.pathname.substring(
      configuration.baseUrl.length,
    );
    return (
      !location.pathname.match(/^\/app\/.*\/device_viewer/) &&
      !location.pathname.match(/^\/app\/.*\/legal/) &&
      !session.activeSubscription &&
      enforceSubscription &&
      !/\/subscriptions\/\d+/.test(location.pathname) && // special handling to access subscription via permissions
      removeBaseUrl(subscriptionEndpoints, configuration.baseUrl).indexOf(
        relevantLocationUrl,
      ) < 0 &&
      removeBaseUrl(unsecuredEndpoints, configuration.baseUrl).indexOf(
        relevantLocationUrl,
      ) < 0
    );
  })();

  useEffect(() => {
    if (shouldRedirect) {
      navigate(`${configuration.baseUrl}/plan_variants`);
    }
  }, [shouldRedirect, navigate, configuration.baseUrl]);

  return shouldRedirect ? null : children;
}
